/**
 * @file merchant_preview.schema.js
 * @description Schéma Zod merchant_preview enrichi P0.4b — parité DivertissementCard.jsx
 *              + slider 5 photos. Décision Q6=A (parité stricte composant existant).
 *
 * @spec RX_v1.1-FINAL + P0.4b enrichissement
 */

const { z } = require('zod');
const { RxV1WrapperSchema, ActionSchema } = require('./rx-v1.schema');

// ─────────────────────────────────────────────────────────────────────────────
// Sous-schémas
// ─────────────────────────────────────────────────────────────────────────────

const CardActionSchema = z.object({
  type: z.enum(['gps', 'call', 'yango', 'details', 'offer', 'copy', 'select']),
  label: z.string().min(1),
  style: z.enum(['primary', 'secondary', 'ghost', 'success', 'warning', 'danger']).default('primary'),
  params: z.record(z.any()).default({}),
});

const MerchantCardSchema = z.object({
  type: z.literal('merchant_card'),
  id: z.string().min(1),
  name: z.string().min(1),
  // Nouveau P0.4b — slider 5 photos (min 1, max 5). Fallback: image_url ancien champ.
  photos: z.array(z.string()).min(1).max(5),
  // Code CIV format `CIV-<VILLE3>-<COMMUNE3>-<5CHARS_UPPERCASE>` (existant OtherAddressPasteModal)
  code_civ: z.string().regex(/^CIV-[A-Z]{3}-[A-Z]{3}-[A-Z0-9]{5}$/),
  category: z.string(),
  category_emoji: z.string().default('📍'),
  commune: z.string(),
  address: z.string().nullable(),
  specialty: z.string().nullable(),
  rating: z.number().min(0).max(5).nullable(),
  hours: z.string().nullable(),
  price_prefix: z.string().default('dès'),
  price_amount: z.number().int().nonnegative().nullable(),
  price_currency: z.string().default('FCFA'),
  badges: z.array(z.string()).default([]),
  has_offer: z.boolean().default(false),
  offer_tier: z.enum(['gold', 'silver', 'bronze']).optional(),
  offer_label: z.string().nullable().optional(),
  offer_code: z.string().nullable().optional(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  phone: z.string().nullable(),
  actions: z.array(CardActionSchema).min(1).max(6),
  accent_color: z.string().default('#F59E0B'), // Accent gold/orange par card, override par catégorie
  primary_action: z.object({
    type: z.enum(['commander', 'reserver', 'none']),
    label: z.string(),
    style: z.enum(['primary_gold', 'primary_green', 'primary_purple']).default('primary_gold'),
    params: z.record(z.any()).default({}),
  }).default({ type: 'none', label: '', style: 'primary_gold', params: {} }),
});

const HeaderSectionSchema = z.object({
  type: z.literal('header'),
  text: z.string().min(1),
});

const MerchantPreviewSectionSchema = z.discriminatedUnion('type', [
  HeaderSectionSchema,
  MerchantCardSchema,
]);

const MerchantPreviewPayloadSchema = RxV1WrapperSchema.extend({
  template: z.literal('merchant_preview'),
  layout: z.literal('card_grid'),
  sections: z.array(MerchantPreviewSectionSchema).refine(
    (sections) => {
      const headers = sections.filter((s) => s.type === 'header');
      const cards = sections.filter((s) => s.type === 'merchant_card');
      const validHeaders = headers.length <= 1;
      const validCards = cards.length <= 10; // P0.4b : jusqu'à 10 (message "10 adresses")
      const hasContent = cards.length >= 1 || (cards.length === 0 && headers.length === 1);
      return validHeaders && validCards && hasContent;
    },
    {
      message:
        'merchant_preview.sections : 0-1 header + soit 1-10 merchant_card, soit 0 card avec header (empty state)',
    },
  ),
});

function safeParseMerchantPreview(payload) {
  return MerchantPreviewPayloadSchema.safeParse(payload);
}

function parseMerchantPreview(payload) {
  return MerchantPreviewPayloadSchema.parse(payload);
}

module.exports = {
  MerchantPreviewPayloadSchema,
  MerchantCardSchema,
  HeaderSectionSchema,
  MerchantPreviewSectionSchema,
  CardActionSchema,
  safeParseMerchantPreview,
  parseMerchantPreview,
  MERCHANT_PREVIEW_TEMPLATE: 'merchant_preview',
};
